import { Component } from '@theme/component';
import { addToCart } from '@theme/cart-api';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { showToast } from '@theme/toast-message';
import { formatMoney, imageUrl, interpolate, replayClass, scrollBehavior, throttle } from '@theme/utilities';

/**
 * @typedef {Object} Variant
 * @property {number} id
 * @property {string[]} options
 * @property {boolean} available
 * @property {number} price
 * @property {number} compare
 * @property {number | null} qty Null when Shopify does not track the inventory
 */

/**
 * @typedef {Object} ProductConfig
 * @property {number} [sizeIndex]
 * @property {number} [currentId]
 * @property {boolean} [hasSingle] Only the default variant
 */

/**
 * @typedef {Object} SuggestProduct
 * @property {string} handle
 * @property {string} title
 * @property {string} url
 * @property {string} [vendor]
 * @property {boolean} available
 * @property {string[]} [tags]
 * @property {{ url?: string } | null} [featured_image]
 */

/** @typedef {import('./product-sticky-bar.js').ProductStickyBar} ProductStickyBar */

/**
 * @typedef {Object} ProductPageRefs
 * @property {HTMLScriptElement} variantsJson
 * @property {HTMLScriptElement} [configJson]
 * @property {HTMLInputElement} variantId Hidden `id` input of the product form
 * @property {HTMLFieldSetElement[]} [options] One fieldset per option, in option order
 * @property {HTMLElement[]} [optionValues] Selected value label inside each fieldset
 * @property {HTMLButtonElement} [addButton]
 * @property {HTMLElement} [addLabel]
 * @property {HTMLElement} [amount]
 * @property {HTMLElement} [comparePrice]
 * @property {HTMLElement} [discount]
 * @property {HTMLElement} [stock]
 * @property {HTMLElement} [stockText]
 * @property {HTMLElement} [stockBar]
 * @property {HTMLElement} [galleryTrack]
 * @property {HTMLElement} [galleryIndex]
 * @property {HTMLElement} [colors]
 * @property {HTMLElement} [colorsList]
 */

/**
 * Product page: variant picker, price and stock, add to cart, sticky quick add, colour siblings,
 * gallery counter.
 *
 * Data attributes: `data-product-id` (recently viewed), `data-sticky-bar` (id of the <product-sticky-bar>), `data-base-title`,
 * `data-vendor`, `data-product-handle`, `data-gender` (colour siblings lookup).
 *
 * @extends {Component<ProductPageRefs>}
 */
class ProductPage extends Component {
  requiredRefs = ['variantsJson', 'variantId'];

  /** @type {Variant[]} */
  #variants = [];

  /** @type {ProductConfig} */
  #config = {};

  /** @type {Variant | null} */
  #current = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {AbortController | null} */
  #colorsFetch = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #doneTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#variants = /** @type {Variant[]} */ (readJson(this.refs.variantsJson) || []);
    this.#config = /** @type {ProductConfig} */ (readJson(this.refs.configJson) || {});

    this.#form = this.refs.variantId.form;
    this.#form?.addEventListener('submit', this.#onSubmit);
    this.refs.galleryTrack?.addEventListener('scroll', this.#onGalleryScroll, { passive: true });

    const selected = this.#selectedOptions();
    this.#render(this.#config.hasSingle ? this.#variants[0] : this.#findVariant(selected), true);
    this.#loadColors();
    if (this.dataset.productId) RecentlyViewed.addProduct(this.dataset.productId);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#form?.removeEventListener('submit', this.#onSubmit);
    this.refs.galleryTrack?.removeEventListener('scroll', this.#onGalleryScroll);
    this.#colorsFetch?.abort();
    clearTimeout(this.#doneTimer);
  }

  /** An option fieldset changed (`on:change="/selectOption"`) */
  selectOption() {
    this.#render(this.#findVariant(this.#selectedOptions()));
  }

  /** The sticky bar's quick add button */
  addFromStickyBar() {
    if (!this.#current) {
      this.#flashSizes();
      return;
    }
    if (typeof this.#form?.requestSubmit === 'function') this.#form.requestSubmit();
    else this.refs.addButton?.click();
  }

  /** @returns {(string | null)[]} */
  #selectedOptions() {
    return (this.refs.options || []).map((set) => {
      const input = set.querySelector('input:checked');
      return input instanceof HTMLInputElement ? input.value : null;
    });
  }

  /**
   * @param {(string | null)[]} selected
   * @returns {Variant | null}
   */
  #findVariant(selected) {
    if (!selected.every((value) => value !== null)) return null;
    return this.#variants.find((variant) => variant.options.every((option, i) => selected[i] === option)) || null;
  }

  /**
   * @param {Variant | null | undefined} variant
   * @param {boolean} [initial]
   */
  #render(variant, initial = false) {
    const { translations } = window.Theme;
    const current = variant || null;
    this.#current = current;
    const selected = this.#selectedOptions();
    const variants = this.#variants;

    // Disable values that cannot complete an available variant with the other choices
    (this.refs.options || []).forEach((set, i) => {
      for (const input of set.querySelectorAll('input')) {
        const possible = variants.some(
          (x) => x.available && x.options.every((o, j) => (j === i ? o === input.value : selected[j] === null || selected[j] === o))
        );
        input.closest('.product-page__size')?.classList.toggle('is-out', !possible);
      }
      const label = this.refs.optionValues?.[i];
      if (label) label.textContent = selected[i] ? `: ${selected[i]}` : '';
    });

    if (current) {
      this.refs.variantId.value = String(current.id);
      const onSale = current.compare > current.price;
      const price = formatMoney(current.price);
      const compare = onSale ? formatMoney(current.compare) : null;
      const { amount, comparePrice, discount } = this.refs;
      if (amount) amount.textContent = price;
      if (comparePrice) {
        comparePrice.hidden = !onSale;
        comparePrice.textContent = compare || '';
      }
      if (discount) {
        discount.hidden = !onSale;
        discount.textContent = onSale ? `-${Math.round(((current.compare - current.price) / current.compare) * 100)}%` : '';
      }
      this.#withStickyBar((bar) => bar.setPrice(price, compare));
      if (!initial) {
        const url = new URL(location.href);
        url.searchParams.set('variant', String(current.id));
        history.replaceState({}, '', url);
      }
    }

    const { addButton, addLabel } = this.refs;
    if (addButton) {
      const soldOut = !variants.some((x) => x.available);
      const needSize = !soldOut && !current && (this.refs.options || []).length > 0;
      addButton.disabled = soldOut || (!!current && !current.available);
      addButton.classList.toggle('is-pending', needSize);
      if (addLabel) {
        addLabel.textContent = soldOut || (current && !current.available) ? translations.soldOut : translations.addToCart;
      }
    }
    this.#setStock(current);
  }

  /** @param {Variant | null} variant */
  #setStock(variant) {
    const { stock, stockText, stockBar } = this.refs;
    if (!stock || !stockText) return;
    const { translations } = window.Theme;
    // Availability is per size: stay hidden until a full variant is chosen (the add button already says "Esaurito" when nothing is left)
    stock.hidden = !variant;
    if (!variant) {
      delete stock.dataset.state;
      return;
    }
    let label = translations.available;
    let percent = 100;
    let state = 'ok';
    if (!variant.available) {
      // One-size products and fully sold-out products read "Esaurito", not "size sold out"
      const allOut = this.#config.hasSingle || !this.#variants.some((x) => x.available);
      label = allOut ? translations.soldOut : translations.sizeSoldOut;
      percent = 0;
      state = 'out';
    } else if (variant.qty !== null && variant.qty <= 3) {
      label = variant.qty === 1 ? translations.lastPiece : interpolate(translations.lastPieces, { count: variant.qty });
      percent = variant.qty * 20;
      state = 'low';
    }
    stockText.textContent = label;
    stock.dataset.state = state;
    stockBar?.style.setProperty('--w', `${percent}%`);
  }

  /** Brings the first missing option into view and flashes it */
  #flashSizes() {
    const sets = this.refs.options || [];
    const set = sets.find((s) => !s.querySelector('input:checked')) || sets[0];
    if (!set) return;
    set.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
    replayClass(set, 'is-flash');
    const first = set.querySelector('.product-page__size:not(.is-out) input');
    if (first instanceof HTMLInputElement) first.focus({ preventScroll: true });
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const current = this.#current;
    if (!current) {
      this.#flashSizes();
      showToast(window.Theme.translations.selectSize);
      return;
    }
    if (!current.available) return;

    const { addButton, addLabel } = this.refs;
    addButton?.classList.add('is-loading');
    const sections = await addToCart([{ id: current.id, quantity: 1 }], { source: 'product-page' });
    addButton?.classList.remove('is-loading');
    if (!sections) return;

    addButton?.classList.add('is-done');
    if (addLabel) addLabel.textContent = window.Theme.translations.addedToCart;
    clearTimeout(this.#doneTimer);
    this.#doneTimer = setTimeout(() => {
      addButton?.classList.remove('is-done');
      this.#render(this.#current, true);
    }, 1800);
  };

  /** Gallery counter (mobile swipe) */
  #onGalleryScroll = throttle(() => {
    const { galleryTrack, galleryIndex } = this.refs;
    if (!galleryTrack || !galleryIndex) return;
    const width = galleryTrack.clientWidth || 1;
    galleryIndex.textContent = String(Math.round(galleryTrack.scrollLeft / width) + 1);
  });

  /**
   * Runs the callback once the sticky bar (moved to <body>) is defined.
   * @param {(bar: ProductStickyBar) => void} callback
   */
  #withStickyBar(callback) {
    const id = this.dataset.stickyBar;
    if (!id || !document.getElementById(id)) return;
    customElements.whenDefined('product-sticky-bar').then(() => {
      const bar = document.getElementById(id);
      if (bar) callback(/** @type {ProductStickyBar} */ (bar));
    });
  }

  /** Colours: sibling products share the title before " - " and the vendor */
  async #loadColors() {
    const { colors, colorsList } = this.refs;
    const base = this.dataset.baseTitle || '';
    if (!colors || !colorsList || base.length <= 2) return;

    this.#colorsFetch?.abort();
    const controller = new AbortController();
    this.#colorsFetch = controller;

    /** @type {SuggestProduct[]} */
    let products;
    try {
      const query = encodeURIComponent(base);
      const response = await fetch(
        `${window.Theme.routes.predictive_search_url}.json?q=${query}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`,
        { signal: controller.signal }
      );
      const data = await response.json();
      products = data?.resources?.results?.products || [];
    } catch {
      return;
    }

    const vendor = (this.dataset.vendor || '').toLowerCase();
    const handle = this.dataset.productHandle;
    const gender = (this.dataset.gender || '').toLowerCase();
    const prefix = `${base.toLowerCase()} - `;
    /** Only siblings for the same gender (or unisex / untagged) @param {SuggestProduct} product */
    const sameGender = (product) => {
      if (gender !== 'uomo' && gender !== 'donna') return true;
      const tag = (product.tags || []).map((t) => String(t).toLowerCase()).find((t) => t.startsWith('gender:'));
      const value = tag ? tag.slice(7) : '';
      return !value || value === gender || value.includes('unisex');
    };
    const items = products.filter(
      (product) =>
        product.handle !== handle &&
        (product.vendor || '').toLowerCase() === vendor &&
        product.title.toLowerCase().startsWith(prefix) &&
        sameGender(product)
    );
    if (!items.length) return;

    for (const product of items) {
      const name = product.title.slice(base.length + 3);
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = `product-page__swatch${product.available ? '' : ' is-out'}`;
      link.href = product.url.split('?')[0] || product.url;
      const image = product.featured_image?.url;
      if (image) {
        const img = document.createElement('img');
        img.src = imageUrl(image, 160);
        img.alt = product.title;
        img.loading = 'lazy';
        link.appendChild(img);
      }
      const label = document.createElement('span');
      label.className = 'screen-reader-only';
      label.textContent = interpolate(window.Theme.translations.colorLabel, { name });
      link.appendChild(label);
      item.appendChild(link);
      colorsList.appendChild(item);
    }
    colors.hidden = false;

    const thumbs = items.slice(0, 5).flatMap((product) => {
      const image = product.featured_image?.url;
      return image ? [{ url: product.url.split('?')[0] || product.url, image, title: product.title }] : [];
    });
    if (thumbs.length) this.#withStickyBar((bar) => bar.addThumbs(thumbs));
  }
}

/**
 * @param {HTMLScriptElement | undefined} script
 * @returns {unknown}
 */
function readJson(script) {
  try {
    return script ? JSON.parse(script.textContent || '') : null;
  } catch {
    return null;
  }
}

if (!customElements.get('product-page')) {
  customElements.define('product-page', ProductPage);
}
